import { useParams } from "react-router-dom";
import React, { useState, useEffect, useRef, useContext } from "react";
import Table from "../../../CommonComponents/Table";
import Search from "../../../CommonComponents/Search";
import ExportPdf from "../../Dashboard/ExportPdf";
import ExportToCsv from "../../Dashboard/ExportToCsv";
import IconButton from "../../../CommonComponents/IconButton";
import Tooltip from "../../../CommonComponents/Tooltip";
import AlertContext from "../../../../context/AlertContext";
import PaginationComponent from "../../../CommonComponents/PaginationComponent";
import Loading from "../../../Notification-Loading/Loading/Loading";
import { useDebouncedValue } from "../../../../Hooks/useDebounceValue";
import useAxiosPrivate from "../../../../Hooks/useAxiosPrivate";
import { RESULT_STATUS, Roles, libTypeIds } from "../../../../constants";
import { getDataFromStorage } from "../../../../util";
import Drawer from "../../../CommonComponents/Drawer";
import useCommonFunctionHooks from "../../../../Hooks/useCommonFunctionHooks";
import AttemptCard from "../../../Student/MockTestPage/AttemptScoreCard";
import GraphForAttempts from "../../../Student/MockTestPage/GraphForAttempts";
import MockTestResultsHooks from "../../../../Hooks/Mock/Student/MockTestResultsHooks";
import { UserReportProvider } from "../../../../context/UserReportContext";
import NameCell from "../../../CommonComponents/NameCell";

const AiInterviewReport = () => {
  const axiosPrivate = useAxiosPrivate();
  const { getCatchBlockDetails } = useCommonFunctionHooks();
  const MockTestResultsHks = MockTestResultsHooks();
  const { id } = useParams();

  const [tableData, setTableData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [searchValue, setSearchValue] = useState("");
  const Alert = useContext(AlertContext);
  const [dataIsLoading, setdataIsLoading] = useState(false);
  const { renderDataToPDF } = ExportPdf();
  const { downloadIndividualExcel } = ExportToCsv();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10; // You can change to 5 or 10
  const [totalPages, setTotalPages] = useState(1);
  const debouncedSearchValue = useDebouncedValue(searchValue, 2000);
  const [showDownloadOptionsDropDown, setShowDownloadOptionsDropDown] = useState(false);
  const downloadIconRef = useRef(null);
  const [reportColumns, setReportColumns] = useState([]);
  const [data, setData] = useState([]);
  const [isHovered, setIsHovered] = useState(null);
  const [attemptedData, setAttemptedData] = useState([]);
  const [testData, setTestData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [clickedLearnerName, setClickedLearnerName] = useState(null);
  const [clickedRow, setClickedRow] = useState([]);
  const [clickedColumn, setClickedColumn] = useState([]);

  const formattedData = tableData.map((item) => {
    const interviewData = {};

    let interviews = item.interviews;

    // 🔥 If interviews is string, parse it
    if (typeof interviews === "string") {
      try {
        interviews = JSON.parse(interviews);
      } catch (err) {
        interviews = [];
      }
    }

    if (Array.isArray(interviews)) {
      interviews.forEach((test) => {
        const totalScore = test.attempt?.[0]?.actualscore ?? "-";
        interviewData[test.name] = totalScore;
      });
    }

    return {
      Name: `${item.firstname ?? ""} ${item.lastname ?? ""}`,
      ...interviewData,
    };
  });

  const fetchAiInterviewReport = React.useCallback(async () => {
    try {
      setdataIsLoading(true);

      const response = await axiosPrivate.get(
        `node/admin/ai-interview/report`,
        {
          params: {
            bid: id,
            page: currentPage,
            limit: pageSize,
            name: debouncedSearchValue,
          }
        }
      );

      const result = response.data;
      // console.log(result);
      if (result?.resultCode === RESULT_STATUS.SUCCESS) {
        const apiData = result.data || [];

        setTotalCount(result.total || 0);
        setTotalPages(Math.ceil((result.total || 0) / pageSize));

        // 🔥 Step 1: Create columns dynamically
        const columns = [{ key: "Name", title: "Name", className: "Name" }];

        apiData.forEach((student) => {
          let interviews = student.interviews;

          if (typeof interviews === "string") {
            try {
              interviews = JSON.parse(interviews);
            } catch {
              interviews = [];
            }
          }

          interviews?.forEach((test) => {
            if (!columns.find((col) => col.key === test.name)) {
              // console.log("test datas : ", test);
              columns.push({
                key: test.name,
                title: test.name,
                className: test.name,
              });
            }
          });
        });

        setReportColumns(columns);

        // 🔥 Step 2: Flatten row data
        const rowData = apiData.map((student) => {
          let interviews = student.interviews;

          if (typeof interviews === "string") {
            try {
              interviews = JSON.parse(interviews);
            } catch {
              interviews = [];
            }
          }

          const row = {
            Name: `${student.firstname ?? ""} ${student.lastname ?? ""}`,
          };

          row["learnerid"] = student.learnerid;

          interviews?.forEach((test) => {
            if (test.attempt.length >= 2) {
              row[`${test.name}_hower`] = true;
            }
            // console.log("test:",test);
            let lastAttempt = test.attempt?.[(test.attempt.length) - 1];
            // console.log("lastAtempt:",lastAttempt);

            if (lastAttempt && lastAttempt.lmpid) {
              const targetScore = lastAttempt.targetscore;
              const score = lastAttempt.actualscore
              // console.log("targetscore", test);
              row[`${test.name}_testid`] = test.testid;
              row[test.name] = `${score}/${targetScore}`;
              row[`${test.name}_status`] = lastAttempt.status;
              row[`${test.name}_lmpid`] = lastAttempt.lmpid;
              row[`${test.name}_lmtid`] = lastAttempt.lmtid;
            }
          });

          return row;
        });

        setData(rowData);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("API Error:", error);
    } finally {
      setdataIsLoading(false);
    }
  }, [currentPage, debouncedSearchValue, id]);

  // console.log("columns:",reportColumns);
  // console.log("data:",data);

  const getTestAttemptDetails = async (learnerid, testid) => {
    Alert.setIsLoaded(true);
    axiosPrivate
      .get(
        `node/learner/${learnerid}/mock/completed?testid=${testid}`,
      )
      .then((res) => {
        if (res.data.resultCode !== RESULT_STATUS.SUCCESS) {
          Alert.setShowNotify({
            show: true,
            title: "Info",
            msg: res.data.message,
          });
        } else {
          // console.log("res:", res);
          const woAIQuestions = res.data.data.tests?.filter(
            (data) => data.aiMode,
          );
          console.log("woAiQuestion:", woAIQuestions);
          // console.log(
          //   "woAIQuestions.attempteddata:",
          //   woAIQuestions[0].attempteddata,
          // );
          setTestData(woAIQuestions);
          setGraphData(
            JSON.parse(woAIQuestions[0].attempteddata)
              .filter((attempt) => attempt.actualscore >= 0)
              .map((attempt, index) => ({
                attempt: index + 1,
                score: attempt.actualscore,
                targetscore: attempt.targetscore,
              })),
          );

          setAttemptedData(JSON.parse(woAIQuestions[0].attempteddata));
        }
      })
      .catch((error) => {
        getCatchBlockDetails(error);
      })
      .finally(() => {
        Alert.setIsLoaded(false);
      });
  };

  // console.log("graphData:", graphData);

  useEffect(() => {
    fetchAiInterviewReport();
  }, [fetchAiInterviewReport]);


  const DownloadIcon = () => (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17 12.5V15.8333C17 16.2754 16.8244 16.6993 16.5118 17.0118C16.1993 17.3244 15.7754 17.5 15.3333 17.5H4.66667C4.22464 17.5 3.80072 17.3244 3.48816 17.0118C3.17559 16.6993 3 16.2754 3 15.8333V12.5M6.33333 8.33333L10.5 12.5M10.5 12.5L14.6667 8.33333M10.5 12.5V2.5"
        stroke="#667085"
        strokeWidth="1.67"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

  const fetchAllAiInterviewDataForDownload = async () => {
    try {
      setdataIsLoading(true);

      const response = await axiosPrivate.get(
        `node/admin/ai-interview/report`,
        {
          params: {
            bid: id,
            isDownload: true,
          },
        }
      );

      if (response?.data?.resultCode === RESULT_STATUS.SUCCESS) {
        return response?.data?.data ?? [];
      }

      return [];
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setdataIsLoading(false);
    }
  };

  const prepareDownloadData = (apiData = []) => {
    if (!Array.isArray(apiData) || apiData.length === 0) {
      return [];
    }

    return apiData.map((student) => {
      let interviews = student.interviews;

      if (typeof interviews === "string") {
        try {
          interviews = JSON.parse(interviews);
        } catch {
          interviews = [];
        }
      }

      const row = {
        Name: `${student.firstname ?? ""} ${student.lastname ?? ""}`,
      };

      interviews?.forEach((test) => {
        const attempts = test.attempt;

        if (!attempts || attempts.length === 0) {
          row[test.name] = "-";
        } else {
          const lastAttempt = attempts[attempts.length - 1];
          row[test.name] = `${lastAttempt.actualscore}/${lastAttempt.targetscore}`;
        }
      });

      return row;
    });
  };


  const handlePdfDownload = async () => {
    const allData = await fetchAllAiInterviewDataForDownload();

    if (allData.length > 0) {
      const downloadData = prepareDownloadData(allData);
      renderDataToPDF(downloadData, "landscape", "a4", "AI_Interview_Report");
    } else {
      Alert.setShowNotify({
        show: true,
        title: "No Data",
        msg: "No data available to download",
      });
    }

    setShowDownloadOptionsDropDown(false);
  };

  const handleCsvDownload = async () => {
    const allData = await fetchAllAiInterviewDataForDownload();

    if (allData.length > 0) {
      const downloadData = prepareDownloadData(allData);
      downloadIndividualExcel(downloadData, "AI_Interview_Report");
    } else {
      Alert.setShowNotify({
        show: true,
        title: "No Data",
        msg: "No data available to download",
      });
    }

    setShowDownloadOptionsDropDown(false);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchValue]);
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadIconRef.current && !downloadIconRef.current.contains(e.target)) {
        setShowDownloadOptionsDropDown(false);
      }
    };

    if (showDownloadOptionsDropDown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDownloadOptionsDropDown]);

  const renderRow = (row, rowIndex, columns) => {
    // console.log("columns:", columns, "row:", row);
    return (
      <>
        <tr key={rowIndex} className="tableBodyRow" >
          {
            columns.map((col) => {
              const isHoverable = row[`${col.key}_hower`];
              const cellId = `${rowIndex}-${col.key}`;
              if(col.key === "Name"){
                return (
                  <UserReportProvider>
                    <NameCell key={cellId} row={row} rowIndex={rowIndex} pos="batch-report-page-container"/>
                  </UserReportProvider>
                );
              }
              return (
                <td className={`tableData ${col.key} ${col.key} ${row[col.key]}  ${row[col.key] ? "isthere" : ""} ${isHovered === cellId && isHoverable ? "hoverable" : ""}`}
                  key={cellId}
                  onMouseEnter={() => setIsHovered(cellId)}
                  onMouseLeave={() => setIsHovered(null)}
                  onClick={() => {
                    if (row[col.key] && !isHoverable && col.key !== "Name") {
                      viewAiInterviewDetails({
                        row,
                        data: row[col.key],
                        col,
                        learnerid: row["learnerid"]
                      })
                    }
                  }}>
                  {row[col.key] ?? "-"}
                  {isHovered === cellId && isHoverable && (
                    <svg
                      width="12"
                      height="10"
                      viewBox="0 0 12 10"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      onClick={() => {
                        if (getDataFromStorage("role") !== Roles.tutor) {
                          console.log("column:", col, row);

                          setFormOpen(true);
                          setClickedLearnerName(row["Name"]);
                          setClickedRow(row);
                          setClickedColumn(col);
                          getTestAttemptDetails(
                            row["learnerid"],
                            row[`${col.key}_testid`],
                          );
                        }
                      }}
                    >
                      <path
                        d="M0.799988 9.13314L4.96665 4.96647L0.799988 0.799805M6.63332 9.13314L10.8 4.96647L6.63332 0.799805"
                        stroke="#667085"
                        stroke-width="1.6"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  )}</td>
              )

            })
          }
        </tr>
      </>
    )
  };


  const viewAiInterviewDetails = ({ row, data, col, learnerid, testData, attemptNo }) => {
    console.log("report clicked", row, data, col, learnerid);
    console.log("row:", clickedRow, "col:", clickedColumn, testData, attemptNo);
    let testid, lmpid, lmtid, tname, libtype, withAI, marks, status, learnerId;
    if (testData) {
      let parsedAttemptedData = JSON.parse(testData.attempteddata);
      let attemptData = parsedAttemptedData.filter((data) => data.atmptcount == attemptNo + 1);
      testid = testData.tid;
      tname = testData.name;
      libtype = testData.libtype;
      withAI = testData.aiMode;
      lmpid = attemptData[0]?.lmpid;
      lmtid = attemptData[0]?.lmtid;
      marks = attemptData[0]?.actualscore;
      learnerId = clickedRow["learnerid"];
    }
    else {
      if (!data) {
        return;
      }
      const reqColumns = Object.keys(row).filter((r) => r.startsWith(col.key));
      libtype = libTypeIds.DESCRIPTIVE;
      const lmtidKey = reqColumns.filter((r) => r.endsWith("lmtid"));
      const lmpidKey = reqColumns.filter((r) => r.endsWith("lmpid"));
      const statusKey = reqColumns.filter((r) => r.endsWith("status"));
      const testKey = reqColumns.filter((r) => r.endsWith("testid"));

      lmpid = row[lmpidKey];
      lmtid = row[lmtidKey];
      marks = data?.split("/")[0];

      testid = row[testKey];
      tname = col.key;
      status = row[statusKey];
      withAI = 1;
      learnerId = row["learnerid"];
    }
    console.log("testid, lmpid, lmtid, tname, libtype, withAI, marks, status, learnerId:", testid, lmpid, lmtid, tname, libtype, withAI, marks, status, learnerId);
    const testdata = {
      id: testid,
      name: tname,
      mode: 2,
      videoqn: 1,
      status: status ?? 2,
      withAI: withAI ?? 0,
      libtype: libtype,
      testtype: libtype,
      viewtest: 1,
      learnerid: learnerId,
      lmtid: lmtid,
      lmpid: lmpid,
      marks: marks,
    }

    MockTestResultsHks.handleView(testdata);
  }


  // console.log("data:", data);

  return (
    <div className="aiinterview-report-container reportpage">
      {dataIsLoading && <Loading overlay />}
      <div className="aiinterview-report-helper-container">
        <Search
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />

        <div
          className="downloadDropdown"
          ref={downloadIconRef}
          style={{ position: "relative", zIndex: 1000 }}
        >
          {showDownloadOptionsDropDown && (
            <div
              className="downloadOptions"
              style={{
                position: "absolute",
                right: 0,
                left: "-80px",
                top: "100%",
                marginTop: "4px",
                backgroundColor: "white",
                border: "1px solid #E4E7EC",
                borderRadius: "8px",
                boxShadow:
                  "0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)",
                padding: "4px",
                minWidth: "120px",
                zIndex: 10,
              }}
            >
              {/* PDF OPTION */}
              <div
                className="option"
                onClick={handlePdfDownload}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  cursor: dataIsLoading ? "not-allowed" : "pointer",
                  borderRadius: "6px",
                  transition: "background-color 0.2s",
                  opacity: dataIsLoading ? 0.5 : 1,
                }}
                onMouseEnter={(e) =>
                  !dataIsLoading &&
                  (e.currentTarget.style.backgroundColor = "#F9FAFB")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <span>
                  <i
                    className="fa fa-file-pdf-o"
                    style={{
                      color: "red",
                      width: "20px",
                      height: "18px",
                    }}
                  ></i>
                </span>
                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500 }}>
                  PDF
                </p>
              </div>

              {/* CSV OPTION */}
              <div
                className="option"
                onClick={handleCsvDownload}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "8px 12px",
                  cursor: dataIsLoading ? "not-allowed" : "pointer",
                  borderRadius: "6px",
                  transition: "background-color 0.2s",
                  opacity: dataIsLoading ? 0.5 : 1,
                }}
                onMouseEnter={(e) =>
                  !dataIsLoading &&
                  (e.currentTarget.style.backgroundColor = "#F9FAFB")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
              >
                <span>
                  <svg
                    width="20"
                    height="18"
                    viewBox="0 0 20 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18.8247 1.50248H12.486V-0.00297165L0 1.41664V16.5437L12.4133 18.003V16.4513H18.8247C19.4322 16.4579 20 15.9033 20 15.3024V2.65797C20 2.05711 19.4322 1.50248 18.8247 1.50248Z"
                      fill="#0B8938"
                    />
                  </svg>
                </span>
                <p style={{ margin: 0, fontSize: "14px", fontWeight: 500 }}>
                  CSV
                </p>
              </div>
            </div>
          )}

          <Tooltip placement="bottom" title="Download" isIcon={true}>
            <IconButton
              hierarchy="tertiaryGrey"
              onClick={() =>
                setShowDownloadOptionsDropDown((prev) => !prev)
              }
            >
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      <Table
        columns={reportColumns}
        data={data}
        renderRow={renderRow}
      />
      {totalPages >= 1 && (
        <PaginationComponent
          current={currentPage}
          total={totalPages}
          className={"aiinterview"}
          onChange={handlePageChange}
        />
      )}
      <Drawer
        open={formOpen}
        anchor="right"
        width="500px"
        title={clickedLearnerName}
        onClose={() => setFormOpen(false)}
        className="ai-interviewreport"
      >
        <div className="header">Attempt History</div>
        <div className="attemptCardContainer">
          {attemptedData.map((attempt, index) => (
            <AttemptCard
              key={index}
              test={testData[0]}
              attemptNo={attempt.atmptcount}
              score={
                attempt.actualscore < 0
                  ? `-/${attempt.targetscore}`
                  : `${attempt.actualscore}/${attempt.targetscore}`
              }
              date={attempt.submitteddate}
              mockReportClick={viewAiInterviewDetails}
            />
          ))}
        </div>
        <div className="attemptGraph">
          {graphData && graphData.length >= 2 && (
            <GraphForAttempts attemptedData={graphData} />
          )}
        </div>
      </Drawer>
    </div>
  );
};

export default AiInterviewReport;